import { useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'

import { verifyPhoneNumber } from '../../actions/phoneNumberActions'

import { Label, InputGroup, Warning } from '../form'
import PhoneNumberField, {
  validPhoneNumber,
} from '../hookForm/PhoneNumberFieldWithCountry'

import PhoneVerificationForm from './PhoneVerificationForm'
import Button from '../display/Button'

const FieldFlexWrapper = styled.div`
  margin: 0 -8px;
  margin-bottom: 8px;
`

const FieldFlex = styled.div`
  margin: 8px;
`

const MobilePhoneNumberForm = ({ noMargin = false }) => {
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)
  const currentUser = auth.currentUser

  const methods = useForm({
    defaultValues: { phone_number: currentUser?.phone_number ?? '' },
  })

  const {
    handleSubmit,
    watch,
    reset,
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    if (!dirtyFields.phone_number) {
      reset({ phone_number: currentUser?.phone_number ?? '' })
    }
  }, [currentUser?.phone_number, dirtyFields.phone_number, reset])

  const watchedPhoneNumber = watch('phone_number')
  const verifiedPhoneNumber = currentUser?.phone_number ?? ''
  const isPhoneNumberValid =
    !!watchedPhoneNumber && !validPhoneNumber(watchedPhoneNumber)

  // The user is "verified" when the server has confirmed their phone AND
  // the form's current value still matches that confirmed number. Editing
  // to a new number un-verifies until they re-verify.
  const isPhoneNumberVerified =
    !!currentUser?.phone_number_verified &&
    watchedPhoneNumber === verifiedPhoneNumber

  const hasUnverifiedChange = watchedPhoneNumber !== verifiedPhoneNumber

  const onSubmit = ({ phone_number }) => {
    dispatch(verifyPhoneNumber(phone_number))
  }

  const submit = handleSubmit(onSubmit)

  const handleKeyDownEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <FormProvider {...methods}>
      <InputGroup noMargin={noMargin}>
        <Label>Mobile phone number</Label>
        <FieldFlexWrapper>
          <FieldFlex
            style={{ flex: 1 }}
            className={
              isPhoneNumberVerified ? 'qa-phone-number-verified' : undefined
            }
            onKeyDown={handleKeyDownEnter}
          >
            <PhoneNumberField
              name="phone_number"
              showAsValid={isPhoneNumberVerified}
              rules={{
                required: 'This field is required',
                validate: (value) => validPhoneNumber(value) || true,
              }}
              rounded
            />
          </FieldFlex>
          {!isPhoneNumberVerified && (
            <FieldFlex style={{ minWidth: 160, flex: 0 }}>
              <Button
                type="button"
                className="inline-button"
                onClick={submit}
                disabled={
                  auth.verifyingPhoneNumber ||
                  auth.phoneNumberVerificationSent ||
                  !isPhoneNumberValid
                }
                rounded
                uppercase={false}
              >
                Verify by SMS
              </Button>
            </FieldFlex>
          )}
        </FieldFlexWrapper>
        {hasUnverifiedChange && (
          <Warning style={{ marginTop: 16 }}>
            You will have to verify this phone number to save it.
          </Warning>
        )}
        {!isPhoneNumberVerified && auth.phoneNumberVerificationSent && (
          <PhoneVerificationForm
            phoneNumber={watchedPhoneNumber}
            roundedInputs
          />
        )}
      </InputGroup>
    </FormProvider>
  )
}

export default MobilePhoneNumberForm
